/**
 * Recovery for stale-deploy chunk failures.
 *
 * This is a hashed-asset Vite SPA served from Firebase Hosting. Every deploy
 * gives the code-split chunks new content hashes and drops the previous files
 * from the CDN. A browser that still holds the *old* `index.html` references
 * hashes that no longer exist, so the next lazy `import()` (a route change, the
 * tenant-selection screen, …) 404s with:
 *
 *   TypeError: Failed to fetch dynamically imported module: .../<name>-<hash>.js
 *
 * Without recovery this bubbles past the (error-blind) `<Suspense>` up to the
 * top-level Sentry `ErrorBoundary` and the user is stuck on an error screen
 * until they manually refresh. (Caused REBAR-IQ-SUPERCHARGED-4.)
 *
 * Vite's module-preload helper dispatches a cancelable `vite:preloadError` on
 * `window` for exactly this failure. We reload once to pull the fresh
 * `index.html` + new chunk hashes, which transparently recovers the user.
 *
 * A sessionStorage timestamp debounces the reload so a genuinely broken deploy
 * or an offline client can't get trapped in a reload loop: if we already
 * reloaded within RELOAD_DEBOUNCE_MS we let the error fall through to the
 * ErrorBoundary (which then offers a *manual* reload) instead of looping.
 *
 * No-op in `vite dev`: the preload helper — and therefore the event — only
 * exists in production builds. Stale chunks are a deploy-time problem anyway.
 */
import * as Sentry from '@sentry/react';

const RELOAD_FLAG_KEY = 'chunk-reload-at';
const RELOAD_DEBOUNCE_MS = 10_000;

/**
 * True for the browser's "couldn't fetch / parse a dynamic import" errors,
 * across Chromium / Firefox / Safari wording and the older webpack phrasing.
 * Used by the ErrorBoundary fallback to decide whether to offer a reload CTA.
 */
export function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';
  return (
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message) ||
    /loading chunk \d+ failed/i.test(message)
  );
}

/** Did we already auto-reload within the debounce window? */
function reloadedRecently(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_FLAG_KEY) ?? '0');
    return Number.isFinite(last) && Date.now() - last < RELOAD_DEBOUNCE_MS;
  } catch {
    // sessionStorage can throw (private mode / blocked storage) — treat as
    // "not recently" so a first failure still gets its one recovery reload.
    return false;
  }
}

function rememberReload(): void {
  try {
    window.sessionStorage.setItem(RELOAD_FLAG_KEY, String(Date.now()));
  } catch {
    // Worst case we lose the loop guard; the ErrorBoundary CTA still backstops.
  }
}

/**
 * Install the global `vite:preloadError` handler. Call once during bootstrap,
 * before the app renders, so it is armed for the very first navigation.
 */
export function setupChunkReloadOnPreloadError(): void {
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadedRecently()) {
      // We already reloaded once and the chunk *still* won't load (broken
      // deploy / offline). Do NOT preventDefault — let Vite re-throw the
      // original chunk error so it reaches the ErrorBoundary cleanly, where
      // `isChunkLoadError` matches and a manual "reload" CTA is shown.
      return;
    }
    // Stale deploy: our cached index.html points at chunk hashes the CDN no
    // longer serves. Own the recovery: suppress Vite's re-throw and reload to
    // pull fresh assets.
    event.preventDefault();
    rememberReload();
    Sentry.addBreadcrumb({
      category: 'chunk-reload',
      level: 'warning',
      message: 'vite:preloadError — reloading to fetch fresh assets after deploy',
      data: { reason: event.payload.message },
    });
    window.location.reload();
  });
}
